import { FEATURES_CONFIG } from "../../featuresConfig";

/**
 * Which popup closes a free-spins round.
 *
 * Three outcomes share one slot at the end of the feature, and exactly one of
 * them opens:
 *
 *   MAX    the win cap was reached
 *   ZERO   the round paid less than 1x the stake  -> BETTER LUCK NEXT TIME
 *   TOTAL  everything else
 *
 * ── Why this is a module and not two inline ternaries ──
 *
 * The round ends in TWO places — the live loop in FreeSpinBasicControlContext
 * and the completion-reload branch in GameViewSimple — and a recovered round
 * must close exactly the way the played one would have. They already drifted
 * once over MAX WIN. So the decision lives here, both call it, and there is no
 * second copy to forget.
 *
 * The denominator also has to be the SAME one the popup prints. Total Win
 * writes "Nx BET" from `win / effectiveBet`; if the threshold divided by
 * anything else the player could be shown BETTER LUCK NEXT TIME above a figure
 * reading 1.4x BET, or a Total Win reading 0.8x. Hence freeSpinOutroBet(),
 * which is what both dialogs feed their multiplier line.
 */

/**
 * The threshold, in multiples of the stake.
 *
 * From the house GDD (Catch the Cod, Free Spins Bonus Game Workflow): "Total
 * Win pop-up is displayed. Alternatively, the Better Luck Next Time pop-up is
 * displayed (when the win is lower than 1x bet)."
 *
 * STRICTLY below. A round paying exactly 1x bet broke even and gets Total Win.
 */
export const BETTER_LUCK_BELOW_X = 1;

/**
 * The threshold actually in force.
 *
 * Normally BETTER_LUCK_BELOW_X. A number in DEV_TOOLS.FS_BETTER_LUCK_BELOW_X
 * overrides it so the popup can be seen without hunting for a losing round —
 * see the comment on that flag. Read per call rather than captured at module
 * load, so flipping the flag does not depend on import order.
 */
const betterLuckBelowX = () => {
  const override = FEATURES_CONFIG.DEV_TOOLS?.FS_BETTER_LUCK_BELOW_X;
  return typeof override === "number" && override > 0
    ? override
    : BETTER_LUCK_BELOW_X;
};

export const FS_OUTRO_POPUP = {
  MAX: "max",
  ZERO: "zero",
  TOTAL: "total",
};

/**
 * The stake the round was PLAYED at — never the price, never the live bet.
 *
 * THE one denominator. The threshold above divides by it and all three outro
 * popups print their "Nx BET" from it, so they cannot contradict each other —
 * a player must never read BETTER LUCK NEXT TIME above a line saying 1.4x BET.
 * It lived inline in each dialog before, three copies of one rule.
 *
 * On a bought bonus price and stake differ: `game.bet` is the 0.10 each free
 * spin was played at, while the 61x price was charged once at purchase. 1x bet
 * means 1x STAKE, so a bought round paying 30x stake is a Total Win even
 * though it cost 61x. That is the written rule, and the only reading that
 * makes a bought and a triggered round behave alike.
 *
 * In replay the live bet is whatever the player happens to be sitting on now,
 * so the replayed round's own `initialBet` wins. Confirmed against PopiPlay's
 * own games, 2026-09-10: their history row divides by the stake and shows the
 * price beside it.
 *
 * Two wrong sources were tried first, recorded so neither comes back:
 *
 *   state.game.bet   the LIVE bet. Right only by coincidence, when the player
 *                    happens to be sitting on the same stake they replayed.
 *                    Change the bet, then replay, and it lies.
 *   replayBet        fetchReplayInit's `data.bet` — the same field that is the
 *                    PRICE on a bought round, so it showed x2.62 where the
 *                    round really paid x1599.70.
 *
 * Falls back to the live bet when the round carries no stake of its own — a
 * standalone replay link has no history row behind it.
 */
export const freeSpinOutroBet = (state) => {
  const liveBet = state?.game?.bet ?? 0;
  if (!state?.replay?.sessionActive) return liveBet;
  const replayBet =
    state?.replay?.endedRound?.initialBet ??
    state?.replay?.enterRound?.initialBet ??
    null;
  return replayBet || liveBet;
};

/**
 * Did the round pay less than the threshold?
 *
 * With no usable stake there is nothing to be a multiple OF, so it falls back
 * to the literal reading — nothing won at all. That keeps a missing bet from
 * turning every round into BETTER LUCK NEXT TIME, which is what a plain
 * `win < bet` would do at bet 0.
 */
export const isBetterLuckRound = (totalWin, bet) => {
  const win = Number(totalWin) || 0;
  const stake = Number(bet) || 0;
  if (stake <= 0) return win <= 0;
  return win < stake * betterLuckBelowX();
};

/**
 * The most a round at this stake can pay, or null when it cannot be known.
 *
 * Lifted out of PopiPlay/RGS, where it was `capForBet`, because the adapter is
 * no longer the only thing that needs it — see isFreeSpinsCapReached. Moved
 * rather than copied: one definition of the cap, and the provider imports it
 * from here so the response and the screen can never disagree about the figure.
 *
 * DEV_TOOLS.FS_MAX_WIN_MULTIPLIER replaces the real multiplier. It only ever
 * LOWERS what the client decides — every caller checks the server's own
 * `win_limit_reached` first — so it can add a max win, never suppress one.
 *
 * @param   {number} bet
 * @returns {number|null}
 */
export const freeSpinsCapForBet = (bet) => {
  const devMultiplier = FEATURES_CONFIG.DEV_TOOLS?.FS_MAX_WIN_MULTIPLIER;
  const maxMultiplier =
    (typeof devMultiplier === "number" && devMultiplier > 0
      ? devMultiplier
      : FEATURES_CONFIG.FREE_SPINS?.MAX_WIN_MULTIPLIER) || 0;
  if (!(bet > 0) || maxMultiplier <= 0) return null;
  return bet * maxMultiplier;
};

/**
 * Has this round hit the win cap?
 *
 * Pot Collection's split, adapted — see the end of
 * PotCollectionSpinEngine._startEndOfBonusSequence, which asks the same
 * question the same way:
 *
 *   the BE flag    `freeSpins.winLimitReached`, raised by the adapter from the
 *                  server's own `outcome.storage.win_limit_reached`.
 *   the FE guard   PC's `capAlreadyHit`: the round total against the cap.
 *
 * Either one is enough, and neither is new — `detectWinLimitReached` in
 * PopiPlay/RGS already makes exactly this split for the RESPONSE it is handed,
 * off the same freeSpinsCapForBet above.
 *
 * This exists beside it because a response is not the only way the round gets
 * here: a completion reload rebuilds the total from storage, and the loop has
 * to be able to ask the question of the state ON SCREEN. Asking it here means
 * the answer follows the total wherever it came from.
 */
export const isFreeSpinsCapReached = (state) => {
  if (state?.freeSpins?.winLimitReached) return true;

  const cap = freeSpinsCapForBet(Number(freeSpinOutroBet(state)) || 0);
  if (cap === null) return false;
  return (Number(state?.freeSpins?.totalWin) || 0) >= cap;
};

/**
 * The cap CANCELS the round — it does not just change the popup.
 *
 * PopiPlay's managers, 2026-09-11. The first build followed Zeus and Pot
 * Collection, where every remaining spin plays; PC is right to keep that, its
 * wins are summed once at the end. Free spins updates the total after every
 * spin, so past the cap the player is already looking at the largest number the
 * game can pay and every further spin is provably meaningless.
 *
 * Nothing else about the ending changes — same popup slot, same wipe, same
 * teardown. It is the ordinary ending, taken early.
 */
export const isFreeSpinsRoundFinished = (remaining, capReached) =>
  !(typeof remaining === "number" && remaining > 0) || !!capReached;

/**
 * The one decision. MAX outranks everything — a capped round is by definition
 * far above the threshold, but the order is explicit so it can never be read
 * as accidental.
 *
 * @returns {"max"|"zero"|"total"}
 */
export const selectFreeSpinOutroPopup = (state) => {
  if (isFreeSpinsCapReached(state)) return FS_OUTRO_POPUP.MAX;
  return isBetterLuckRound(state?.freeSpins?.totalWin, freeSpinOutroBet(state))
    ? FS_OUTRO_POPUP.ZERO
    : FS_OUTRO_POPUP.TOTAL;
};
